import { useCallback, useMemo, useReducer } from 'react';
import {
  buildMemberSubsetScope,
  descendantKeys,
  MemberRow,
  parentKeysOf,
  MemberIndex,
  MemberSubsetOp,
  MemberSubsetScope,
  seedExpansion
} from 'src/components/star-grid/utils/subset-editor/memberSubset';
import { descendantUids, SubsetOp } from 'src/components/star-grid/utils/subset-editor/memberSubsetEntries';

/**
 * The subset editor's draft state.
 *
 * `expanded` and `checked` hold POSITION keys (root→member id chains), never bare member ids: a
 * multi-parent member appears at several positions, and acting on it under one parent must not act
 * on it under another.
 *
 * `scope` is the built subset — `null` means "all members", i.e. unrestricted, which is how the API
 * reads an empty selection. Two interaction styles:
 *   * while `scope` is null the pane is the whole hierarchy and `checked` IS the membership, so
 *     ticking boxes and pressing OK works as a plain picker;
 *   * once an operation has run, the pane is the scope and its VISIBLE ROWS are the membership (as
 *     in the TM1 editor), and `checked` becomes a selection tool for further operations.
 * Operations clear `checked` so the two never disagree about what is selected.
 */
export interface MemberSubsetDraft {
  readonly expanded: ReadonlySet<string>;
  readonly checked: ReadonlySet<string>;
  readonly scope: MemberSubsetScope | null;
  /**
   * ROW UIDS removed from the view — not position keys. Kept beside the scope so Remove survives an
   * operation.
   *
   * Uids, because `Parent` and drill put the same POSITION on screen twice: a position key names both rows,
   * so removing one took the other instead. A uid names exactly one row.
   */
  readonly excluded: ReadonlySet<string>;
  /**
   * `All` view: every distinct member at one level, no rollups. Separate from `scope` because it is a
   * whole-view mode rather than an operation on a selection.
   */
  readonly flat: boolean;
  /**
   * Splices into the row list — `Parent` and expand — in invocation order. See `applyOps`.
   *
   * Stored as instructions rather than as rows, because the row set is rebuilt from the hierarchy on
   * every render: both ops are SPLICES into that list (TM1ROLLUP, DRILLDOWNMEMBER), so they have to be
   * replayed against whatever the builders produce, in order, each finding its row in the list the
   * previous ones left.
   */
  readonly ops: readonly SubsetOp[];
  /**
   * Bumped by every VIEW-level action (All, Show All Members, Remove All, Reset view, an operation).
   *
   * The grid seeds its expansion once per mount, so a deliberate view change has to remount it. This
   * is what the remount key keys off — deliberately not `expanded.size`, which would remount on every
   * expand the USER performs and yank the scroll position out from under them.
   */
  readonly nonce: number;
}

export type MemberSubsetAction =
  | { readonly type: 'setExpanded'; readonly key: string; readonly expanded: boolean }
  | { readonly type: 'setChecked'; readonly keys: readonly string[] }
  | { readonly type: 'applyOp'; readonly op: MemberSubsetOp; readonly selected: readonly string[]; readonly index: MemberIndex }
  | {
      readonly type: 'remove';
      readonly uids: readonly string[];
      readonly withDescendants: boolean;
      /**
       * EVERY row in the list, not just the searched ones — descendants and the tick cleanup are both read off
       * them, and both are about rows that exist rather than rows that happen to be on screen.
       */
      readonly rows: readonly MemberRow[];
    }
  | { readonly type: 'showAll'; readonly flat: boolean; readonly expanded?: readonly string[] }
  | { readonly type: 'expandAll'; readonly keys: readonly string[] }
  | { readonly type: 'collapseAll'; readonly keys: readonly string[] }
  | { readonly type: 'removeAll'; readonly uids: readonly string[] }
  | { readonly type: 'addParents'; readonly key: string; readonly index: MemberIndex }
  | { readonly type: 'drill'; readonly uid: string; readonly key: string }
  | { readonly type: 'reset' };

const toggle = (set: ReadonlySet<string>, key: string): ReadonlySet<string> => {
  const next = new Set(set);
  if (!next.delete(key)) next.add(key);
  return next;
};

export const EMPTY_MEMBER_SUBSET: MemberSubsetDraft = {
  expanded: new Set<string>(),
  checked: new Set<string>(),
  scope: null,
  excluded: new Set<string>(),
  flat: false,
  ops: [],
  nonce: 0
};

/** Pure, so every operation is unit-testable without a network or a DOM. */
export const memberSubsetReducer = (state: MemberSubsetDraft, action: MemberSubsetAction): MemberSubsetDraft => {
  switch (action.type) {
    case 'setExpanded': {
      // Absolute, not a toggle: this mirrors the grid's own state, and a toggle would drift out of
      // step the moment an event arrived twice.
      if (state.expanded.has(action.key) === action.expanded) return state;
      return { ...state, expanded: toggle(state.expanded, action.key) };
    }
    case 'setChecked': {
      // Wholesale, because ticking a parent cascades to every descendant — reconciling that one
      // toggle at a time would take a stream of events to land on the right answer.
      const incoming = new Set(action.keys);
      if (incoming.size === state.checked.size && [...incoming].every((key) => state.checked.has(key))) return state;
      // Kept in the order the keys were first ticked, so a caller can list them as picked.
      const next = new Set([...state.checked].filter((key) => incoming.has(key)));
      for (const key of action.keys) next.add(key);
      return { ...state, checked: next };
    }
    case 'applyOp': {
      const scope = buildMemberSubsetScope(action.index, action.op, action.selected);
      if (scope.anchors.length === 0) return state;
      /*
       * Did the SELECTION reach below the anchors? That is what tells the two readings of an operation apart, and
       * it survives only here — `topmostKeys` prunes descendants away to build `anchors`, so by then it is gone.
       *
       *   * Nothing ticked, right-clicking a row: the context menu passes `[clicked.key]` alone, so the selection
       *     equals the anchors. The user pointed at ONE member, so the operation decides the expansion and a
       *     parent you had open comes back CLOSED.
       *   * A ticked rollup: `groupSelects: 'descendants'` cascades, so the selection carries the subtree. The
       *     user had those rows selected and was looking at them, so their expansion is preserved.
       */
      const anchors = new Set(scope.anchors);
      const selectionReachedBelow = action.selected.some((key) => !anchors.has(key));
      // Exclusions are cleared: they were removals from the PREVIOUS view, and carrying them into a
      // freshly scoped one would hide members the user never removed from it.
      return {
        scope,
        /*
         * MERGED when the selection reached below the anchors, REPLACED when it did not — see above.
         *
         * Merging preserves what the user was looking at: keeping a rollup whose children were expanded and
         * selected leaves them expanded, because Keep was not a request to fold them away. Replacing makes the
         * variant authoritative: right-clicking one member asks for that member, so plain `Keep` gives it back
         * closed however open it was, which is what separates `keep` from `keepChildren` and `keepDescendants`.
         */
        expanded: selectionReachedBelow ? new Set([...state.expanded, ...seedExpansion(action.index, scope)]) : seedExpansion(action.index, scope),
        // The selection SURVIVES. Clearing it was an over-correction for the OK count jumping after an
        // operation; that came from deriving membership from visible rows, and is fixed at the source.
        checked: state.checked,
        excluded: new Set<string>(),
        // An operation is a statement about the hierarchy, so it leaves the flat view and drops
        // anything `Parent` had spliced into the previous one.
        flat: false,
        ops: [],
        nonce: state.nonce + 1
      };
    }
    case 'remove': {
      const next = new Set(state.excluded);
      for (const uid of action.uids) next.add(uid);
      if (action.withDescendants) for (const uid of descendantUids(action.uids, action.rows)) next.add(uid);
      /*
       * Removed rows cannot stay ticked, or they would still be committed as members. Untick a POSITION only when
       * THIS remove took one of its rows AND none of its rows survive: the same position can be on screen twice,
       * and removing one copy is not a statement about the other.
       *
       * A tick with no row in the list at all is left alone. That is a position outside a Keep's scope, where the
       * selection deliberately survives the operation — "not in the list" there means "not shown", not "removed".
       */
      const removedKeys = new Set(action.rows.filter((row) => next.has(row.uid) && !state.excluded.has(row.uid)).map((row) => row.key));
      const surviving = new Set(action.rows.filter((row) => !next.has(row.uid)).map((row) => row.key));
      const checked = new Set([...state.checked].filter((key) => !removedKeys.has(key) || surviving.has(key)));
      return { ...state, excluded: next, checked };
    }
    case 'showAll':
      /*
       * Both `All` (flat) and `Show All Members` (nested) restore the whole hierarchy, so they clear
       * any scope and removals. The SELECTION survives — changing what you are looking at is not a
       * deselect.
       */
      return {
        ...EMPTY_MEMBER_SUBSET,
        checked: state.checked,
        flat: action.flat,
        // `Show All Members` passes every position, so expansion is STATE rather than an imperative
        // grid call. Calling expandAll() from the click handler ran before React re-rendered with the
        // restored rows, so it expanded the old (post-Remove-All, empty) grid and the new rows
        // arrived collapsed.
        expanded: new Set(action.expanded ?? []),
        nonce: state.nonce + 1
      };
    case 'expandAll':
      /*
       * Opens subtrees WITHOUT restoring the hierarchy — the one thing `showAll` cannot do, since it clears the
       * scope and removals by design. Everything except expansion is left alone, so this composes with a Keep
       * rather than undoing it.
       *
       * Unions rather than replaces: expanding a selection must not close the branches already open.
       *
       * `nonce` still advances, because the grid reads its expansion seed once per mount — this IS a view
       * action, so a remount is correct here.
       */
      return { ...state, expanded: new Set([...state.expanded, ...action.keys]), nonce: state.nonce + 1 };
    case 'collapseAll': {
      /*
       * The inverse of `expandAll`: SUBTRACTS the given keys instead of adding them, and like it leaves the
       * scope, removals and selection alone, so it composes with a Keep rather than undoing it. `Reset View` is
       * the one that clears everything.
       *
       * Subtracting rather than clearing is what lets it act on a selection: collapsing one rollup must not
       * close the branches the user has open elsewhere. The caller passes every currently-open key when nothing
       * is selected, which collapses the lot.
       */
      const expanded = new Set(state.expanded);
      for (const key of action.keys) expanded.delete(key);
      // `nonce` advances so the grid re-reads its seed; without it the rows stay open until the next view action.
      return { ...state, expanded, nonce: state.nonce + 1 };
    }
    case 'removeAll': {
      // Everything on screen is excluded, leaving the grid's header with no rows. Ticks on removed
      // rows go with them, or they would still be committed as members.
      // Added to the earlier removals: replacing them restored every one, and an empty second click restored everything.
      const excluded = new Set([...state.excluded, ...action.uids]);
      if (excluded.size === state.excluded.size && state.checked.size === 0) return state;
      return { ...state, excluded, checked: new Set<string>(), nonce: state.nonce + 1 };
    }
    case 'addParents': {
      /*
       * Acts on the row that was CLICKED, never on the selection. Ticking a rollup cascades to its
       * whole subtree, so reading the selection collected the parents of dozens of descendants the
       * user never pointed at.
       *
       * Recorded as an instruction and replayed by `applyRollups`. NOTHING is hidden, moved or appended:
       * the splice alone produces Arc's layout, because a row whose parent has been put out of reach
       * detaches to level 0 when the list is walked, and its former siblings detach with it.
       */
      // A root has no parent, so this is a no-op rather than a splice of nothing.
      if (parentKeysOf(action.index, action.key).length === 0) return state;
      /*
       * Recorded ONCE per member, as `drill` is. A repeat has nothing to add — the parents are already on
       * screen at level 0 — and drawing them twice there is noise. `applyOps` is independently safe
       * against a duplicate log, so this is the interaction rule rather than the correctness guard.
       */
      if (state.ops.some((op) => op.kind === 'rollup' && op.key === action.key)) return state;
      return { ...state, ops: [...state.ops, { kind: 'rollup', key: action.key }], nonce: state.nonce + 1 };
    }
    case 'drill': {
      /*
       * Expanding a row the grid sees as a leaf. Recorded ONCE: replaying the same drill twice would
       * splice duplicate uids, and AG Grid silently DROPS rows whose ids collide.
       *
       * No nonce bump — this is the user opening a row, not a view-level change, and remounting would
       * yank their scroll position. The position key joins `expanded` so the row survives a later
       * remount still open.
       */
      if (state.ops.some((op) => op.kind === 'drill' && op.uid === action.uid)) return state;
      return {
        ...state,
        ops: [...state.ops, { kind: 'drill', uid: action.uid }],
        expanded: new Set([...state.expanded, action.key])
      };
    }
    case 'reset':
      /*
       * Back to exactly the view the editor opened with: the whole hierarchy, collapsed, nothing
       * scoped, removed or flattened.
       *
       * Restores the VIEW, not the SELECTION. Clearing `checked` here left the grid still showing
       * ticks (it re-seeds from the current selection on remount) while the counts read none, so the
       * two disagreed.
       */
      return { ...EMPTY_MEMBER_SUBSET, checked: state.checked, nonce: state.nonce + 1 };
    default:
      return state;
  }
};

// ─── Undo / redo ─────────────────────────────────────────────────────────────

/**
 * One restorable point.
 *
 * `nonce` is EXCLUDED. It is a remount counter for the grid rather than state, and it must only ever
 * advance — see the restore in :func:`memberSubsetHistoryReducer`.
 */
export type MemberSubsetSnapshot = Omit<MemberSubsetDraft, 'nonce'>;

/** The draft plus the two stacks around it. `present` is what the editor renders. */
export interface MemberSubsetHistory {
  readonly past: readonly MemberSubsetSnapshot[];
  readonly present: MemberSubsetDraft;
  readonly future: readonly MemberSubsetSnapshot[];
}

export type MemberSubsetHistoryAction =
  | MemberSubsetAction
  | { readonly type: 'undo' }
  | { readonly type: 'redo' }
  /** Drop both stacks — the context they describe has gone. See the modal's hierarchy/view effect. */
  | { readonly type: 'forget' };

/**
 * The actions that leave a restorable point.
 *
 * Selection is deliberately absent: ticking a box is not an edit to the subset, and undo reversing a tick
 * would be surprising. `checked` still travels IN the snapshot, because `applyOp` and `remove` change it as a
 * side effect and an undo has to put it back.
 *
 * An explicit list rather than a denylist, so a new action forces whoever adds it to decide.
 */
const RECORDED: ReadonlySet<MemberSubsetAction['type']> = new Set([
  'applyOp',
  'remove',
  'removeAll',
  'showAll',
  'expandAll',
  'collapseAll',
  'addParents',
  'drill',
  'reset',
  'setExpanded'
]);

/** Deep enough for the session without pinning every set the user ever expanded. */
const HISTORY_LIMIT = 50;

const snapshot = (draft: MemberSubsetDraft): MemberSubsetSnapshot => {
  const { nonce: _nonce, ...rest } = draft;
  return rest;
};

/**
 * Undo/redo as SNAPSHOTS around the existing reducer, which is untouched.
 *
 * Snapshots rather than inverse commands, because `applyOp` clears `ops` AND `excluded` wholesale and
 * `showAll` resets nearly everything — an inverse would have to reconstruct all of it to undo one Keep,
 * while a snapshot restores it for free. It costs little: the reducer never mutates, so a snapshot shares
 * structure with its neighbours rather than copying them.
 *
 * There is no separate log of user actions. `ops` (Parent and drill) is a field INSIDE each snapshot, so
 * undoing a `Parent` means rendering from an `ops` array one entry shorter — the row list is derived from it
 * on every render anyway, so nothing has to un-splice anything.
 */
export const memberSubsetHistoryReducer = (state: MemberSubsetHistory, action: MemberSubsetHistoryAction): MemberSubsetHistory => {
  if (action.type === 'forget') {
    if (state.past.length === 0 && state.future.length === 0) return state;
    return { past: [], present: state.present, future: [] };
  }

  if (action.type === 'undo' || action.type === 'redo') {
    const undoing = action.type === 'undo';
    const stack = undoing ? state.past : state.future;
    const restored = undoing ? stack[stack.length - 1] : stack[0];
    if (restored === undefined) return state;
    /*
     * `nonce` ADVANCES on a restore; it is never replayed from the snapshot.
     *
     * The grid remounts on `nonce` and reads its expansion seed once per mount, so handing back an old value
     * would leave the grid showing the expansion it already had and the restored view would never appear.
     */
    const present: MemberSubsetDraft = { ...restored, nonce: state.present.nonce + 1 };
    const moved = snapshot(state.present);
    return undoing
      ? { past: state.past.slice(0, -1), present, future: [moved, ...state.future] }
      : { past: [...state.past, moved], present, future: state.future.slice(1) };
  }

  const present = memberSubsetReducer(state.present, action);
  /*
   * IDENTITY, not deep equality. The reducer returns the SAME object when an action changes nothing, which is
   * what makes this cheap — and it is load-bearing for REDO rather than an optimisation: the grid re-seeds its
   * expansion on every remount and mirrors that back through `setExpanded`, so without this those echoes would
   * record entries and clear the redo branch the moment you undid anything.
   */
  if (present === state.present) return state;
  if (!RECORDED.has(action.type)) return { ...state, present };
  // A new edit invalidates the redo branch: what it led to is no longer reachable from here.
  return { past: [...state.past, snapshot(state.present)].slice(-HISTORY_LIMIT), present, future: [] };
};

/** Draft state for one open subset editor. Discarded on Cancel; the caller commits on OK. */
export const useMemberSubsetDraft = (initial: MemberSubsetDraft = EMPTY_MEMBER_SUBSET) => {
  const [history, dispatch] = useReducer(memberSubsetHistoryReducer, { past: [], present: initial, future: [] });
  const draft = history.present;
  const setExpanded = useCallback((key: string, expanded: boolean) => dispatch({ type: 'setExpanded', key, expanded }), []);
  const setChecked = useCallback((keys: readonly string[]) => dispatch({ type: 'setChecked', keys }), []);
  const applyOp = useCallback(
    (op: MemberSubsetOp, selected: readonly string[], index: MemberIndex) => dispatch({ type: 'applyOp', op, selected, index }),
    []
  );
  const remove = useCallback(
    (uids: readonly string[], withDescendants: boolean, rows: readonly MemberRow[]) => dispatch({ type: 'remove', uids, withDescendants, rows }),
    []
  );
  const showAll = useCallback((flat: boolean, expanded?: readonly string[]) => dispatch({ type: 'showAll', flat, expanded }), []);
  const expandAll = useCallback((keys: readonly string[]) => dispatch({ type: 'expandAll', keys }), []);
  const collapseAll = useCallback((keys: readonly string[]) => dispatch({ type: 'collapseAll', keys }), []);
  const removeAll = useCallback((uids: readonly string[]) => dispatch({ type: 'removeAll', uids }), []);
  const addParents = useCallback((key: string, index: MemberIndex) => dispatch({ type: 'addParents', key, index }), []);
  const drill = useCallback((uid: string, key: string) => dispatch({ type: 'drill', uid, key }), []);
  const reset = useCallback(() => dispatch({ type: 'reset' }), []);
  const undo = useCallback(() => dispatch({ type: 'undo' }), []);
  const redo = useCallback(() => dispatch({ type: 'redo' }), []);
  const forgetHistory = useCallback(() => dispatch({ type: 'forget' }), []);
  const canUndo = history.past.length > 0;
  const canRedo = history.future.length > 0;
  return useMemo(
    () => ({
      draft,
      dispatch,
      setExpanded,
      setChecked,
      applyOp,
      remove,
      showAll,
      expandAll,
      collapseAll,
      removeAll,
      addParents,
      drill,
      reset,
      undo,
      redo,
      forgetHistory,
      canUndo,
      canRedo
    }),
    [
      draft,
      setExpanded,
      setChecked,
      applyOp,
      remove,
      showAll,
      expandAll,
      collapseAll,
      removeAll,
      addParents,
      drill,
      reset,
      undo,
      redo,
      forgetHistory,
      canUndo,
      canRedo
    ]
  );
};
